import {
  Box,
  Card,
  Group,
  Text,
  Progress,
  Badge,
  Table,
  Skeleton,
  rem,
  ThemeIcon,
  Indicator,
  Stack,
  SegmentedControl,
  Flex,
  Loader,
} from "@mantine/core";
import { IconChartBar, IconCheck, IconExclamationMark } from "@tabler/icons-react";
import { useMemo, useState } from "react";

export default function EvidencesDistributionCard({loading, pei, evidenciasPorKpi, }) {
  
  const [displayType, setDisplayType] = useState('tag'); // 'tag' o 'table'

  const rows = useMemo(() => {
    if (!pei?.Objetivos) return [];
    const temp = [];

    pei.Objetivos.forEach((obj) => {
      obj.criterios.forEach((cri) => {
        cri.kpiList.forEach((kpi) => {
          const count = evidenciasPorKpi?.[kpi.Id] ?? 0;
          const status = count > 0 ? "completed" : "pending";
          temp.push({
            objetivo: obj.numObjetivo,
            criterio: cri.numCriterio,
            kpiId: kpi.Id,
            kpi: kpi.numKpi,
            evidences: count,
            status,
            isFirstInObjective: false,
            isFirstInCriterio: false,
          });
        });
      });
    });

    for (let i = 0; i < temp.length; i++) {
      temp[i].isFirstInObjective =
          i === 0 || temp[i - 1].objetivo !== temp[i].objetivo;
      temp[i].isFirstInCriterio =
          i === 0 ||
          temp[i - 1].criterio !== temp[i].criterio ||
          temp[i - 1].objetivo !== temp[i].objetivo;
    }

    return temp;
  }, [pei, evidenciasPorKpi]);

  const totalKPIs = rows.length;
  const completedKPIs = rows.filter((r) => r.status === "completed").length;
  const completionPercentage = totalKPIs > 0 ? (completedKPIs / totalKPIs) * 100 : 0;

  return (
    <Card withBorder radius="md">
      <Card.Section p={'lg'}>
        <Flex
          direction={{ base: 'column', sm: 'row' }}
          gap={{ base: 'sm', sm: 'md' }}
          justify='space-between'
        >
          <Box flex={4}>
            <Text size="lg" fw={600}>
              Distribución de Evidencias
            </Text>
            <Text size="sm" c="dimmed">
              Matriz de evidencias por objetivo, criterio y KPI
            </Text>
          </Box>

          <Stack flex={2}>
            <Box>
              <Text size={rem(25)} fw={700} c={'#3590f3ff'} ta={'end'}>
                {Math.round(completionPercentage)}%
              </Text>
              <Progress value={completionPercentage} size="md" color="#3590f3ff" />
            </Box>
            <SegmentedControl
              value={displayType}
              onChange={setDisplayType}
              size="xs"
              fullWidth
              withItemsBorders={false}
              data={[
                { label: 'Etiquetas', value: 'tag' },
                { label: 'Tabla', value: 'table' }
              ]}
            />
          </Stack>
        </Flex>
      </Card.Section>

      <Card.Section>
        <Box>
          {displayType === 'tag' ? (
            <Flex 
              px={'lg'}
              py={'sm'}
              gap="sm"
              justify="center"
              align="flex-start"
              direction="row"
              wrap="wrap"
            >
              {loading ? (
                <Loader type="dots"/>
              ):
                rows.map((row, index) => {
                  let label = `${row.objetivo}.${row.criterio}.${row.kpi} - `
                  label += row.evidences > 0 ? `${row.evidences} evidencia${row.evidences !== 1 ? 's' : ''}` : 'Pendiente';
                  const color = row.status === 'completed' ? 'green.6' : 'red.6';

                  return(
                    <Badge key={index} color={color} radius="md">{label}</Badge>
                  )
                })
              }
              
            </Flex>
          ):(
            <Table.ScrollContainer maxHeight={rem(300)}>
              <Table horizontalSpacing="md" highlightOnHover layout="fixed">
                <Table.Thead>
                  <Table.Tr bg={'#fafcfd'} h={45}>
                    <Table.Th ta="center" fw={600} w="20%">
                      Objetivo
                    </Table.Th>
                    <Table.Th ta="center" fw={600} w="20%">
                      Criterio
                    </Table.Th>
                    <Table.Th ta="center" fw={600} w="20%">
                      KPI
                    </Table.Th>
                    <Table.Th ta="center" fw={600} w="40%">
                      Estado de Evidencias
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>

                <Table.Tbody>
                  {loading ? (
                    Array.from({ length: 7 }).map((_, rowIndex) => (
                      <Table.Tr key={`skeleton-${rowIndex}`}>
                        {Array.from({ length: 5 }).map((__, colIndex) => (
                          <Table.Td key={`skeleton-${rowIndex}-${colIndex}`}>
                            <Skeleton height={16} />
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    ))
                  ) : (
                    pei?.Objetivos?.map((objetivo, objetivoIndex) =>
                      objetivo.criterios.map((criterio, criterioIndex) =>
                        criterio.kpiList.map((kpi, kpiIndex) => {
                          const count = (evidenciasPorKpi && evidenciasPorKpi[kpi.Id]) || 0;
                          const isFirstObj = criterioIndex === 0 && kpiIndex === 0;
                          const isFirstCri = kpiIndex === 0;

                          const objetivoRowSpan = objetivo.criterios.reduce((sum, c) => sum + (c.kpiList?.length || 0), 0);
                          const criterioRowSpan = criterio.kpiList?.length || 0;

                          return (
                            <Table.Tr key={kpi.Id} style={{ transition: 'background-color 150ms ease' }}>
                              {isFirstObj && (
                                <Table.Td
                                  rowSpan={objetivoRowSpan}
                                  style={{ verticalAlign: 'middle' }}
                                >
                                  <Text ta="center" fw={700} c="gray.7" size="xl">
                                    {objetivo.numObjetivo}
                                  </Text>
                                </Table.Td>
                              )}

                              {isFirstCri && (
                                <Table.Td
                                  rowSpan={criterioRowSpan}
                                  style={{ verticalAlign: 'middle' }}
                                >
                                  <Text ta="center" fw={600} c="gray.7" size="lg">
                                    {criterio.numCriterio}
                                  </Text>
                                </Table.Td>
                              )}

                              <Table.Td>
                                <Text ta="center" fw={500} c="gray.7" size="md">
                                  {kpi.numKpi}
                                </Text>
                              </Table.Td>

                              <Table.Td ta="center" fw={700}>
                                {count === 0 ? (
                                  <Group gap={5} justify="center" wrap="nowrap">
                                    <ThemeIcon radius="xl" variant="outline" size={'xs'} color="red"><IconExclamationMark stroke={2} height={'70%'} width={'70%'}/></ThemeIcon>
                                    <Badge color="red.6" radius="md">
                                      Pendiente
                                    </Badge>
                                  </Group>
                                ) : (
                                  <Group gap={5} justify="center" wrap="nowrap">
                                    <ThemeIcon radius="xl" variant="outline" size={'xs'} color="green"><IconCheck stroke={2} height={'70%'} width={'70%'}/></ThemeIcon>
                                    <Badge color="green.6" radius="md">
                                      {count} evidencia{count !== 1 ? 's' : ''}
                                    </Badge>
                                  </Group>
                                )}
                              </Table.Td>
                            </Table.Tr>
                          );
                        })
                      )
                    )
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Box>
      </Card.Section>

      <Card.Section p={'lg'} bg={'#fafcfd'} withBorder>
        <Group justify="space-between">
          <Group gap="xs" c="dimmed">
            <IconChartBar size={16} />
            <Text size="sm">
              Total: <Text span fw={600}>{completedKPIs}</Text> de{" "}
              <Text span fw={600}>{totalKPIs}</Text> KPIs completados
            </Text>
          </Group>

          <Group gap="lg">
            <Group gap={6}>
              <Badge size={8} circle color={'green'}/>
              <Text size="sm" c="dimmed" fw={600}>
                {completedKPIs} Completados
              </Text>
            </Group>
            <Group gap={6}>
              <Badge size={8} circle color={'red'}/>
              <Text size="sm" c="dimmed" fw={600}>
                {totalKPIs - completedKPIs} Pendientes
              </Text>
            </Group>
          </Group>
        </Group>
      </Card.Section>
    </Card>
  );
}
